import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DropDownList, LookupDialogDimensions } from '@core/interfaces';
import { ConfirmationService, EventService, GridService, ListService, NotificationService, fromList } from '@core/services';

import { endpoints } from '@environment';

import { SelectedRow } from '#types/grid';
import {
  OrderAddItemRequest,
  OrderAddItemResponseBody,
  OrderCreateRequest,
  OrderCreateResponseBody,
  OrderDeleteItemRequest,
  OrderDeleteItemResponseBody,
  OrderDetailUpdateItemRequest,
  OrderDetailUpdateItemResponseBody,
  OrderDuplicateCheckRequest,
  OrderDuplicateCheckResponseBody,
  OrderInquiryAttachmentRequest,
  OrderInquiryAttachmentResponseBody,
  OrderInquiryCancelOrderRequest,
  OrderInquiryCancelOrderResponseBody,
  OrderInquiryDetailRequest,
  OrderInquiryDetailResponseBody,
  OrderInquiryDetailResponseRow,
  OrderInquiryDownloadAttachmentRequest,
  OrderInquiryDownloadAttachmentResponseBody,
  OrderInquiryLockOrderRequest,
  OrderInquiryLockOrderResponseBody,
  OrderInquiryRequest,
  OrderInquiryResponseBody,
  OrderInquiryResponseRow,
  OrderInquiryShippingDetailRequest,
  OrderInquiryShippingDetailResponseBody,
  OrderInquiryUpdateRequest,
  OrderInquiryUpdateResponseBody,
} from '#types/order';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@order/constants';
import { DetailAddItemDialogComponent } from '@order/detail/add-item/add-item-dialog.component';
import { OrderInquiryAttachmentDialogComponent } from '@order/inquiry-attachment-dialog/inquiry-attachment-dialog.component';
import { OrderInquiryShippingDetailDialogComponent } from '@order/inquiry-shipping-detail-dialog/inquiry-shipping-detail-dialog.component';
import { OrderActions, fromOrder } from '@order/state';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  orderItemStatusList$ = this.store.select(fromList.selectOrderItemStatusList);
  orderPriorityList$ = this.store.select(fromList.selectOrderPriorityList);
  orderStatusList$ = this.store.select(fromList.selectOrderStatusList);
  orderTypeList$ = this.store.select(fromList.selectOrderTypeList);

  orderItemStatusDropdownList$ = new BehaviorSubject<DropDownList>(null);
  orderPriorityDropdownList$ = new BehaviorSubject<DropDownList>(null);
  orderStatusDropdownList$ = new BehaviorSubject<DropDownList>(null);
  orderTypeDropdownList$ = new BehaviorSubject<DropDownList>(null);

  orderInquiryShippingDetailDialogFilters$ = new BehaviorSubject<{ orderid: number; shipid: number }>(null);
  orderInquiryShippingDetailDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  orderInquiryAttachmentDialogFilters$ = new BehaviorSubject<{ orderid: number; shipid: number }>(null);
  orderInquiryAttachmentDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  addItemDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  addItemDialogStep0Dimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  addItemDialogStep1Dimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  createOrderCheckPanelSize$ = new Subject<void>();

  inquiryGridPanelHeight$ = new BehaviorSubject<number>(0);
  detailGridPanelHeight$ = new BehaviorSubject<number>(0);

  detail$ = this.store.select(fromOrder.selectDetail);

  updateItem$ = this.store.select(fromOrder.selectUpdateItem);
  updateItemDisplay$ = this.store.select(fromOrder.selectUpdateItemDisplay);
  udpateItemLotNumber$ = this.store.select(fromOrder.selectUpdatemLotNumber);
  updateItemUom$ = this.store.select(fromOrder.selectUpdateItemUOM);
  udpateItemWeightOrUom$ = this.store.select(fromOrder.selectUpdateItemWeightOrUOM);
  udpateItemQuanity$ = this.store.select(fromOrder.selectUpdateItemQuantity);
  udpateItemWeight$ = this.store.select(fromOrder.selectUpdateItemWeight);
  udpateItemWeightUOM$ = this.store.select(fromOrder.selectUpdateItemWeightUom);
  updateItemItem$ = this.store.select(fromOrder.selectUpdateItemItem);

  addItem$ = this.store.select(fromOrder.selectAddItem);
  addItemDisplay$ = this.store.select(fromOrder.selectAddItemDisplay);
  addItemStep$ = this.store.select(fromOrder.selectAddItemStep);
  addItemItem$ = this.store.select(fromOrder.selectAddItemItem);
  addItemDescription$ = this.store.select(fromOrder.selectAddItemDescription);
  addItemLotNumber$ = this.store.select(fromOrder.selectAddItemLotNumber);
  addItemPassthru$ = this.store.select(fromOrder.selectAddItemPassthru);
  addItemQuantity$ = this.store.select(fromOrder.selectAddItemQuantity);
  addItemUom$ = this.store.select(fromOrder.selectAddItemUom);
  addItemWeight$ = this.store.select(fromOrder.selectAddItemWeight);
  addItemWeightOrUom$ = this.store.select(fromOrder.selectAddItemWeightOrUom);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private listService: ListService,
    private store: Store,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private notificationService: NotificationService,
    private eventService: EventService,
    private gridService: GridService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });
  }

  requestLists() {
    this.store.dispatch(OrderActions.RequestLists());
  }

  orderInquiry(payload: OrderInquiryRequest) {
    return this.http.post(endpoints.order.inquiry.inquiry, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_ORDER_INQUIRY),
      map((response: OrderInquiryResponseBody) => response)
    );
  }

  orderInquiryUpdate(payload: OrderInquiryUpdateRequest) {
    return this.http.post(endpoints.order.inquiry.updateOrder, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_UPDATE_ROW),
      map((response: OrderInquiryUpdateResponseBody) => response)
    );
  }

  orderInquiryShippingDetail(payload: OrderInquiryShippingDetailRequest) {
    return this.http.post(endpoints.order.inquiry.shippingDetail, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_ORDER_INQUIRY_SHIPPING_DETAIL),
      map((response: OrderInquiryShippingDetailResponseBody) => response)
    );
  }

  orderInquiryAttachment(payload: OrderInquiryAttachmentRequest) {
    return this.http.post(endpoints.order.inquiry.attachment, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_ORDER_INQUIRY_ATTACHMENT),
      map((response: OrderInquiryAttachmentResponseBody) => response)
    );
  }

  orderInquiryDownloadAttachment(payload: OrderInquiryDownloadAttachmentRequest) {
    return this.http.post(endpoints.order.inquiry.downloadAttachment, payload, { withCredentials: true, responseType: 'blob' }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_DOWNLOAD_ATTACHMENT),
      map((response: OrderInquiryDownloadAttachmentResponseBody) => response)
    );
  }

  orderInquiryCancelOrder(payload: OrderInquiryCancelOrderRequest) {
    return this.http.post(endpoints.order.inquiry.cancelOrder, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_CANCEL_ORDER),
      map((response: OrderInquiryCancelOrderResponseBody) => response)
    );
  }

  orderInquiryLockOrder(payload: OrderInquiryLockOrderRequest) {
    return this.http.post(endpoints.order.inquiry.lockOrder, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_LOCK_ORDER),
      map((response: OrderInquiryLockOrderResponseBody) => response)
    );
  }

  orderDetail(payload: OrderInquiryDetailRequest) {
    return this.http.post(endpoints.order.inquiry.detail.detail, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_ORDER_INQUIRY_DETAIL),
      map((response: OrderInquiryDetailResponseBody) => response)
    );
  }

  orderCreate(payload: OrderCreateRequest) {
    return this.http.post(endpoints.order.create, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_CREATE),
      map((response: OrderCreateResponseBody) => response)
    );
  }

  orderUpdateItem(payload: OrderDetailUpdateItemRequest) {
    return this.http.post(endpoints.order.updateItem, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_UPDATE_ITEM),
      map((response: OrderDetailUpdateItemResponseBody) => response)
    );
  }

  orderAddItem(payload: OrderAddItemRequest) {
    return this.http.post(endpoints.order.addItem, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_ADD_ITEM),
      map((response: OrderAddItemResponseBody) => response)
    );
  }

  orderDeleteItem(payload: OrderDeleteItemRequest) {
    return this.http.post(endpoints.order.deleteItem, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_DELETE_ITEM),
      map((response: OrderDeleteItemResponseBody) => response)
    );
  }

  orderDuplicateCheck(payload: OrderDuplicateCheckRequest) {
    return this.http.post(endpoints.order.duplicateCheck, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_ORDER_DUPLICATE_CHECK),
      map((response: OrderDuplicateCheckResponseBody) => response)
    );
  }

  orderInquiryShippingDetailDialog(orderid: number, shipid: number, config: DynamicDialogConfig = {}): DynamicDialogRef {
    this.orderInquiryShippingDetailDialogFilters$.next({ orderid, shipid });

    return this.dialogService.open(OrderInquiryShippingDetailDialogComponent, {
      header: this.translate('order.shipping_details_dialog.dialog.title'),
      closeOnEscape: false,
      baseZIndex: 0,
      autoZIndex: true,
      dismissableMask: false,
      rtl: false,
      styleClass: 'lookup-dialog large-dialog',
      transitionOptions: '200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
      closable: true,
      showHeader: true,
      modal: true,
      resizable: true,
      draggable: true,
      keepInViewport: true,
      ...config,
    });
  }

  orderInquiryAttachmentDialog(orderid: number, shipid: number, config: DynamicDialogConfig = {}): DynamicDialogRef {
    this.orderInquiryAttachmentDialogFilters$.next({ orderid, shipid });

    return this.dialogService.open(OrderInquiryAttachmentDialogComponent, {
      header: this.translate('order.attachment_dialog.dialog.title'),
      closeOnEscape: false,
      baseZIndex: 0,
      autoZIndex: true,
      dismissableMask: false,
      rtl: false,
      styleClass: 'lookup-dialog large-dialog',
      transitionOptions: '200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
      closable: true,
      showHeader: true,
      modal: true,
      resizable: true,
      draggable: true,
      keepInViewport: true,
      ...config,
    });
  }

  orderDetailUpdateItemDialog() {
    this.store.dispatch(OrderActions.UpdateItemDialogOpen());
  }

  orderDetailAddItemDialog(config: DynamicDialogConfig = {}): DynamicDialogRef {
    this.store.dispatch(OrderActions.AddItemDialogOpen());

    return this.dialogService.open(DetailAddItemDialogComponent, {
      header: this.translate('order.add_item_dialog.title'),
      closeOnEscape: false,
      baseZIndex: 0,
      autoZIndex: true,
      dismissableMask: false,
      rtl: false,
      styleClass: 'lookup-dialog large-dialog step-0',
      transitionOptions: '200ms cubic-bezier(0.25, 0.8, 0.25, 1)',
      closable: true,
      showHeader: true,
      modal: true,
      resizable: true,
      draggable: true,
      keepInViewport: true,
      ...config,
    });
  }

  initializeDropdownLists() {
    this.orderTypeList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.orderTypeDropdownList$.next(
        this.listService.getDropdownList(list, {
          key3: 'value',
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
          header3: this.translate('core.services.list.dropdown.header.abbreviation'),
        })
      );
    });

    this.orderStatusList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.orderStatusDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.orderPriorityList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.orderPriorityDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.orderItemStatusList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.orderItemStatusDropdownList$.next(
        this.listService.getDropdownList(list, {
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }

  duplicateOrderConfirmation(
    payload: OrderCreateRequest,
    uniqueOrderIdentifier: string,
    duplicateReferenceYesNoWarning: string,
    _duplicateCount: number
  ) {
    let key: string;

    if (uniqueOrderIdentifier === 'P') {
      key = 'reference_po';
    } else {
      key = 'reference';
    }

    if (duplicateReferenceYesNoWarning === 'Y') {
      // duplicates allowed, but warn that order will be created on hold
      this.store.dispatch(OrderActions.CreateOrderRequest({ payload }));
    } else if (duplicateReferenceYesNoWarning === 'H') {
      // duplicates allowed, but warn that order will be created on hold
      this.confirmationService.confirm({
        title: this.translate(`order.create_order.duplicate_order.${key}.hold.title`),
        message: this.translate(`order.create_order.duplicate_order.${key}.hold.message`),
        icon: 'fad fa-triangle-exclamation',
        acceptLabel: this.translate(`order.create_order.duplicate_order.${key}.hold.accept`),
        rejectLabel: this.translate(`order.create_order.duplicate_order.${key}.hold.reject`),
        defaultFocus: 'reject',
        accept: () => {
          this.store.dispatch(OrderActions.CreateOrderRequest({ payload }));
        },
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
      });
    } else if (duplicateReferenceYesNoWarning === 'W') {
      // duplicates allowed, but warn user before proceeding
      this.confirmationService.confirm({
        title: this.translate(`order.create_order.duplicate_order.${key}.warn.title`),
        message: this.translate(`order.create_order.duplicate_order.${key}.warn.message`),
        icon: 'fad fa-triangle-exclamation',
        acceptLabel: this.translate(`order.create_order.duplicate_order.${key}.warn.accept`),
        rejectLabel: this.translate(`order.create_order.duplicate_order.${key}.warn.reject`),
        defaultFocus: 'reject',
        accept: () => {
          this.store.dispatch(OrderActions.CreateOrderRequest({ payload }));
        },
        acceptButtonStyleClass: 'p-button-warning',
        rejectButtonStyleClass: 'p-button-secondary',
      });
    } else if (duplicateReferenceYesNoWarning === 'N') {
      // duplicates not allowed
      this.confirmationService.confirm({
        title: this.translate(`order.create_order.duplicate_order.${key}.error.title`),
        message: this.translate(`order.create_order.duplicate_order.${key}.error.message`),
        icon: 'fad fa-triangle-exclamation',
        rejectLabel: this.translate(`order.create_order.duplicate_order.${key}.error.reject`),
        rejectButtonStyleClass: 'p-button-secondary',
        acceptVisible: false,
      });
    } else {
      this.notificationService.error({
        message: `${this.translate('order.create_order.duplicate_order.unknown.message')}: ${duplicateReferenceYesNoWarning}`,
      });
    }
  }

  duplicateOrderUpdateConfirmation(
    payload: OrderInquiryResponseRow,
    uniqueOrderIdentifier: string,
    duplicateReferenceYesNoWarning: string,
    _duplicateCount: number,
    index: number
  ) {
    let key: string;

    if (uniqueOrderIdentifier === 'P') {
      key = 'reference_po';
    } else {
      key = 'reference';
    }

    if (duplicateReferenceYesNoWarning === 'Y') {
      // duplicates allowed, but warn that order will be created on hold
      this.gridService.rowEditSave$.next({ index, data: payload });
      this.store.dispatch(OrderActions.OrderInquiryUpdateRequestData({ payload }));
    } else if (duplicateReferenceYesNoWarning === 'H') {
      // duplicates allowed, but warn that order will be created on hold
      this.confirmationService.confirm({
        title: this.translate(`order.inquiry.update_order.duplicate_order.${key}.hold.title`),
        message: this.translate(`order.inquiry.update_order.duplicate_order.${key}.hold.message`),
        icon: 'fad fa-triangle-exclamation',
        acceptLabel: this.translate(`order.inquiry.update_order.duplicate_order.${key}.hold.accept`),
        rejectLabel: this.translate(`order.inquiry.update_order.duplicate_order.${key}.hold.reject`),
        defaultFocus: 'reject',
        accept: () => {
          this.gridService.rowEditSave$.next({ index, data: payload });
          this.store.dispatch(OrderActions.OrderInquiryUpdateRequestData({ payload }));
        },
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
      });
    } else if (duplicateReferenceYesNoWarning === 'W') {
      // duplicates allowed, but warn user before proceeding
      this.confirmationService.confirm({
        title: this.translate(`order.inquiry.update_order.duplicate_order.${key}.warn.title`),
        message: this.translate(`order.inquiry.update_order.duplicate_order.${key}.warn.message`),
        icon: 'fad fa-triangle-exclamation',
        acceptLabel: this.translate(`order.inquiry.update_order.duplicate_order.${key}.warn.accept`),
        rejectLabel: this.translate(`order.inquiry.update_order.duplicate_order.${key}.warn.reject`),
        defaultFocus: 'reject',
        accept: () => {
          this.gridService.rowEditSave$.next({ index, data: payload });
          this.store.dispatch(OrderActions.OrderInquiryUpdateRequestData({ payload }));
        },
        acceptButtonStyleClass: 'p-button-warning',
        rejectButtonStyleClass: 'p-button-secondary',
      });
    } else if (duplicateReferenceYesNoWarning === 'N') {
      // duplicates not allowed
      this.confirmationService.confirm({
        title: this.translate(`order.inquiry.update_order.duplicate_order.${key}.error.title`),
        message: this.translate(`order.inquiry.update_order.duplicate_order.${key}.error.message`),
        icon: 'fad fa-triangle-exclamation',
        rejectLabel: this.translate(`order.inquiry.update_order.duplicate_order.${key}.error.reject`),
        rejectButtonStyleClass: 'p-button-secondary',
        acceptVisible: false,
      });
    } else {
      this.notificationService.error({
        message: `${this.translate('order.inquiry.update_order.duplicate_order.unknown.message')}: ${duplicateReferenceYesNoWarning}`,
      });
    }
  }

  deleteItem(selectedRow: SelectedRow<OrderInquiryDetailResponseRow>) {
    const { orderid, shipid, item, lotnumber, qtyorder, uomabbrev } = selectedRow.data;

    let message: string;

    if (lotnumber) {
      message = this.translate('order.detail.delete_item.message_item_lot', {
        orderid: `${orderid}`,
        shipid: `${shipid}`,
        item,
        lotnumber,
        qtyorder: `${qtyorder}`,
        uomabbrev,
      });
    } else {
      message = this.translate('order.detail.delete_item.message_item', {
        orderid: `${orderid}`,
        shipid: `${shipid}`,
        item,
        qtyorder: `${qtyorder}`,
        uomabbrev,
      });
    }

    this.confirmationService.confirm({
      title: this.translate('order.detail.delete_item.title'),
      message,
      icon: 'fad fa-triangle-exclamation',
      acceptLabel: this.translate('order.detail.delete_item.accept_button'),
      rejectLabel: this.translate('order.detail.delete_item.reject_button'),
      defaultFocus: 'reject',
      accept: () => {
        this.store.dispatch(OrderActions.OrderDeleteItemRequest({ orderid, shipid, item, lotNumber: lotnumber }));
      },
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
    });
  }

  download(filename: string, mimeType: string, data: any) {
    const blob = new Blob([data], {
      type: mimeType,
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;

    a.download = filename;

    document.body.appendChild(a);
    a.style.display = 'none';
    a.click();
    a.remove();

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  }
}
